<script setup>
import { useForm } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import { useToast } from '@/composables/useToast'

const props = defineProps({
  fonction:      { type: Object, default: () => ({}) },
  can_see_costs: { type: Boolean, default: false },
  errors:        { type: Object, default: () => ({}) },
  index_url:     { type: String, default: '/type_fonctions' },
})

const form = useForm({
  code:         props.fonction.code         || '',
  description:  props.fonction.description  || '',
  taux_horaire: props.fonction.taux_horaire || '',
})

const { error: toastError } = useToast()

const fieldLabels = {
  code:         'Code',
  description:  'Description',
  taux_horaire: 'Taux horaire',
}

function showErrors() {
  const merged = { ...props.errors, ...form.errors }
  Object.entries(merged).forEach(([k, v]) => {
    const label = fieldLabels[k] || k
    const msgs = Array.isArray(v) ? v : [v]
    msgs.forEach(m => toastError(`${label} : ${m}`))
  })
}

function submit() {
  form.transform(data => ({ type_fonction: data })).patch(`/type_fonctions/${props.fonction.id}`, {
    preserveScroll: true,
    onError: () => showErrors(),
  })
}

const fieldError = (field) =>
  form.errors[field] || props.errors[field]?.[0] || null
</script>

<template>
  <AppLayout>
    <template #topbar-left>
      <div class="text-xs font-black uppercase tracking-widest text-outline">
        Professions &rsaquo; Modifier la profession
      </div>
    </template>

    <div class="p-8">
      <div class="max-w-4xl mx-auto">

        <!-- Page Header -->
        <div class="mb-8 flex justify-between items-end">
          <div>
            <h1 class="text-3xl font-black text-on-surface tracking-tight uppercase">
              Modifier la profession
            </h1>
            <p class="text-outline text-sm font-black uppercase tracking-widest mt-1">
              {{ fonction.code }}
            </p>
          </div>
          <a
            :href="index_url"
            class="flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest text-outline hover:text-on-surface bg-surface-container-low border border-outline-variant/10 rounded-md transition-all"
          >
            <span class="material-symbols-outlined text-base">arrow_back</span>
            Retour
          </a>
        </div>

        <!-- Form Card -->
        <div class="bg-surface-container rounded-xl p-8 border-l-4 border-primary shadow-2xl shadow-black/40">
          <form @submit.prevent="submit" class="space-y-8">

            <!-- Code + Description -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Code <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.code"
                  type="text"
                  maxlength="15"
                  placeholder="ex. MAÇON"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  :class="fieldError('code') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('code')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('code') }}</p>
              </div>

              <div class="md:col-span-2">
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Description <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.description"
                  type="text"
                  placeholder="Description de la profession"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  :class="fieldError('description') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('description')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('description') }}</p>
              </div>
            </div>

            <!-- Taux Horaire -->
            <div v-if="can_see_costs" class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label class="block text-[10px] font-black text-outline uppercase tracking-widest mb-3">
                  Taux horaire ($/h) <span class="text-error">*</span>
                </label>
                <input
                  v-model="form.taux_horaire"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  class="w-full bg-surface-container-highest border-none rounded-md text-on-surface placeholder:text-outline/50 px-4 py-3 focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                  :class="fieldError('taux_horaire') ? 'ring-2 ring-error/40' : ''"
                />
                <p v-if="fieldError('taux_horaire')" class="mt-1.5 text-[10px] text-error font-bold">{{ fieldError('taux_horaire') }}</p>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex items-center gap-4 pt-6">
              <button
                type="submit"
                :disabled="form.processing"
                class="flex-1 machined-sheen text-on-primary font-black py-4 rounded-md hover:brightness-110 active:scale-[0.98] transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 uppercase tracking-widest text-sm"
              >
                <span v-if="form.processing" class="material-symbols-outlined text-base animate-spin">progress_activity</span>
                <span v-else class="material-symbols-outlined text-base">save</span>
                Enregistrer les modifications
              </button>
              <a
                :href="index_url"
                class="px-8 py-4 bg-transparent border border-outline-variant/30 text-outline font-black rounded-md hover:bg-surface-container-highest hover:text-on-surface transition-all uppercase tracking-widest text-sm text-center"
              >
                Annuler
              </a>
            </div>

          </form>
        </div>

        <!-- Footer -->
        <footer class="mt-12 pb-6 text-center text-outline text-[10px] uppercase tracking-widest font-black">
          Copyright &copy; 2016-{{ new Date().getFullYear() }} aurapport.com inc.
        </footer>

      </div>
    </div>
  </AppLayout>
</template>
